import type { ReactNode } from "react";

/**
 * The desktop workspace grid.
 *
 * Owns the `salesko-workspace` section and its two class forms
 * (`is-detail-collapsed`, `is-private-agent`) and nothing else — no state, no
 * data. The four slots are rendered as DIRECT children of the grid so the CSS
 * that positions them keeps matching, including the direct-child selectors
 * `.salesko-workspace.is-private-agent > .private-agent-workspace`. A slot may
 * pass a fragment (fragments add no DOM node); it must not be wrapped here.
 *
 * `detailPanelOpen` lives in the caller because entity selection sets it; this
 * component only reflects it in the grid form.
 */
export type WorkspaceShellProps = {
  ariaLabel: string;
  /** Rail + stage, or the Private Agent workspace. */
  body: ReactNode;
  /** The `#workspace-detail` side panel. */
  detail: ReactNode;
  detailOpen: boolean;
  header: ReactNode;
  /** Dialogs and floating surfaces that are not grid areas. */
  overlays: ReactNode;
  privateAgent: boolean;
};

export function WorkspaceShell({
  ariaLabel,
  body,
  detail,
  detailOpen,
  header,
  overlays,
  privateAgent
}: WorkspaceShellProps) {
  return (
    <section
      className={`salesko-workspace${detailOpen ? "" : " is-detail-collapsed"}${privateAgent ? " is-private-agent" : ""}`}
      aria-label={ariaLabel}
    >
      {header}
      {body}
      {detail}
      {overlays}
    </section>
  );
}
